import type { BoardFreshnessDivision, BoardFreshnessRun } from '@erp/rpc';

/**
 * Whether the buying board's data is from today's refresh (GEN-3149). "Today"
 * is the browser's local calendar day: a timestamp counts when it falls on the
 * same local date as `now`.
 */

/** `current` is the only state that does not warn. */
export type RunFreshness =
  'current' | 'failed' | 'unfinished' | 'unrecorded' | 'not_today' | 'missing';
export type StockFreshness = 'current' | 'not_today' | 'missing';

export interface RunGroup {
  /** Stable React key: the run's batch id, or `none`. */
  key: string;
  run: BoardFreshnessRun | null;
  state: RunFreshness;
  divisionNames: string[];
}

export interface StockGroup {
  /** Stable React key: the update's minute, or `none`. */
  key: string;
  /** ISO timestamp, or null when no division in the group has a snapshot. */
  syncedAt: string | null;
  state: StockFreshness;
  divisionNames: string[];
}

export interface BoardFreshness {
  runs: RunGroup[];
  stock: StockGroup[];
  /** True when any run or stock group is not `current`. */
  stale: boolean;
  /**
   * The board's "last updated" time: the oldest of the divisions' last
   * completed runs, so no division reads fresher than it is. Null when any
   * division has no completed run.
   */
  lastUpdatedAt: string | null;
  /**
   * The plain-language warning: the last completed update, or any division's
   * stock, is not from today. Unlike `stale`, a failed run after an earlier
   * completed one today does not count; only admins see run outcomes.
   */
  notUpdatedToday: boolean;
}

export function isSameLocalDay(iso: string, now: Date): boolean {
  const date = new Date(iso);
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}

export function runFreshness(
  run: BoardFreshnessRun | null,
  now: Date,
): RunFreshness {
  if (run === null) return 'missing';
  if (run.status === 'failed') return 'failed';
  // Still running, or its process died before it could record an outcome.
  if (run.status === 'running') return 'unfinished';
  if (run.status === null) {
    // Recorded before outcomes were tracked: only its start is known.
    return isSameLocalDay(run.startedAt, now) ? 'unrecorded' : 'not_today';
  }
  return run.completedAt !== null && isSameLocalDay(run.completedAt, now)
    ? 'current'
    : 'not_today';
}

export function stockFreshness(
  syncedAt: string | null,
  now: Date,
): StockFreshness {
  if (syncedAt === null) return 'missing';
  return isSameLocalDay(syncedAt, now) ? 'current' : 'not_today';
}

/** Feed writes within the same minute read as one update. */
function minuteKey(iso: string | null): string {
  return iso === null ? 'none' : iso.slice(0, 16);
}

function groupBy<T>(
  divisions: BoardFreshnessDivision[],
  key: (division: BoardFreshnessDivision) => string,
  make: (division: BoardFreshnessDivision) => T,
): (T & { key: string; divisionNames: string[] })[] {
  const groups = new Map<
    string,
    T & { key: string; divisionNames: string[] }
  >();
  for (const division of divisions) {
    const k = key(division);
    const existing = groups.get(k);
    if (existing) existing.divisionNames.push(division.divisionName);
    else
      groups.set(k, {
        ...make(division),
        key: k,
        divisionNames: [division.divisionName],
      });
  }
  return [...groups.values()];
}

/**
 * Collapses divisions that share a run, or whose stock updated in the same
 * minute, so one overnight refresh reads as one line.
 */
export function assessBoardFreshness(
  divisions: BoardFreshnessDivision[],
  now: Date,
): BoardFreshness {
  const runs = groupBy(
    divisions,
    (d) => d.latestRun?.batchId ?? 'none',
    (d) => ({ run: d.latestRun, state: runFreshness(d.latestRun, now) }),
  );
  const stock = groupBy(
    divisions,
    (d) => minuteKey(d.stockSyncedAt),
    (d) => ({
      syncedAt: d.stockSyncedAt,
      state: stockFreshness(d.stockSyncedAt, now),
    }),
  );
  const stale =
    runs.some((g) => g.state !== 'current') ||
    stock.some((g) => g.state !== 'current');
  const lastUpdatedAt = oldestCompletion(divisions);
  const notUpdatedToday =
    lastUpdatedAt === null ||
    !isSameLocalDay(lastUpdatedAt, now) ||
    stock.some((g) => g.state !== 'current');
  return { runs, stock, stale, lastUpdatedAt, notUpdatedToday };
}

export function oldestCompletion(
  divisions: BoardFreshnessDivision[],
): string | null {
  let oldest: string | null = null;
  for (const division of divisions) {
    const at = division.lastCompletedAt;
    if (at === null) return null;
    if (oldest === null || Date.parse(at) < Date.parse(oldest)) oldest = at;
  }
  return oldest;
}
